/**
 * EditRolesModal — replace a user's roles.
 *
 * Backend: PATCH /console/users/{id} with `{ roles: EditableUserRole[] }`.
 * The server does a full replace (does NOT silently preserve student/teacher),
 * so the modal must include every role the user should keep. `super_admin`
 * is rejected at the schema layer and is therefore never offered.
 *
 * Auth: caller is responsible for gating visibility — the backend allows
 * only super-admin or tenant_admin to mutate roles, and tenant-admins are
 * additionally scoped to their own tenant.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Modal } from '@shared-ui/components';
import { userApi } from '../../api/userApi';
import type { UserRole } from '../../api/auth';
import { authApi } from '../../api/auth';
import { isPlatformRole } from '../../config/adminRoles';

/** Roles the modal can toggle. Mirrors backend `EditableUserRoleSchema`
 *  (UserRole minus `super_admin`). */
type EditableRole = Exclude<UserRole, 'super_admin'>;

const ASSIGNABLE_ROLES: { value: EditableRole; label: string; hint: string }[] = [
  { value: 'student',       label: 'Student',       hint: 'Use the student app to solve questions and view progress.' },
  { value: 'teacher',       label: 'Teacher',       hint: 'Use the teacher app to assign tests and track student progress.' },
  { value: 'tenant_admin',  label: 'Tenant admin',  hint: 'Owner of the tenant. Implies the other tenant roles there, and can grant them.' },
  { value: 'user_admin',    label: 'User admin',    hint: 'Manage this tenant\u2019s users, admins, and feedback.' },
  { value: 'content_admin', label: 'Content admin', hint: 'Author this tenant\u2019s questions, question sets, and tips.' },
  { value: 'prompt_admin',  label: 'Prompt admin',  hint: 'Platform role \u2014 prompts, pipelines, and providers.' },
  { value: 'eval_admin',    label: 'Eval admin',    hint: 'Platform role \u2014 evals, scenario evals, and model runs.' },
  { value: 'ops_admin',     label: 'Ops admin',     hint: 'Platform role \u2014 monitoring, logs, and operational settings.' },
];

/**
 * Rows to render. A non-super-admin sees the roles they may grant, PLUS any
 * platform role the target already holds — shown but locked.
 *
 * Hiding a held role is worse than showing it: `roles` is submitted as a full
 * replacement, so the hidden role rides along in the payload and the server
 * rejects the whole edit over something the caller cannot see. Locked-and-
 * visible explains the constraint instead of failing it.
 */
function visibleRoles(isSuperAdmin: boolean, current: readonly string[]): {
  row: typeof ASSIGNABLE_ROLES[number]; locked: boolean;
}[] {
  if (isSuperAdmin) return ASSIGNABLE_ROLES.map(row => ({ row, locked: false }));
  return ASSIGNABLE_ROLES
    .filter(r => !isPlatformRole(r.value) || current.includes(r.value))
    .map(row => ({ row, locked: isPlatformRole(row.value) }));
}

interface EditRolesModalProps {
  isOpen: boolean;
  userId: string;
  userName: string;
  /** The user's current `roles[]` (full list including app roles). */
  currentRoles: UserRole[];
  onClose: () => void;
}

export function EditRolesModal({ isOpen, userId, userName, currentRoles, onClose }: EditRolesModalProps) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<EditableRole>>(new Set());
  const [error, setError] = useState('');

  // Sync from currentRoles when the modal opens or the source list changes.
  useEffect(() => {
    if (!isOpen) return;
    const next = new Set<EditableRole>();
    for (const r of currentRoles) {
      if (ASSIGNABLE_ROLES.some(a => a.value === r)) next.add(r as EditableRole);
    }
    setSelected(next);
    setError('');
  }, [isOpen, currentRoles]);

  // Diff against the user's current editable roles to know if we should
  // enable Save (no-op patches are wasteful and confusing).
  const currentEditableSet = new Set<EditableRole>(
    currentRoles.filter((r): r is EditableRole => ASSIGNABLE_ROLES.some(a => a.value === r)),
  );
  const isDirty =
    selected.size !== currentEditableSet.size ||
    [...selected].some(r => !currentEditableSet.has(r));

  const saveMut = useMutation({
    mutationFn: () => userApi.updateUser(userId, { roles: [...selected] }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-detail', userId] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-admins'] });
      onClose();
    },
    onError: (err: Error) => setError(err.message),
  });

  function toggle(role: EditableRole) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(role)) next.delete(role); else next.add(role);
      return next;
    });
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Edit roles — ${userName}`}
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => { setError(''); saveMut.mutate(); }}
            disabled={!isDirty || saveMut.isPending}
            loading={saveMut.isPending}
          >
            Save
          </Button>
        </>
      }
    >
      <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 'var(--spacing-sm)' }}>
        Select the roles this user should have. The full set replaces the user's current
        roles — uncheck anything that should be removed.
        {authApi.getUser()?.isSuperAdmin !== true && (
          <> Platform roles (prompt, eval, ops) are shown locked — only a super-admin can change them.</>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' }}>
        {visibleRoles(authApi.getUser()?.isSuperAdmin === true, currentRoles).map(({ row: r, locked }) => (
          <label
            key={r.value}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--spacing-sm)',
              padding: 'var(--spacing-sm)',
              borderRadius: 6,
              background: 'var(--bg-secondary)',
              cursor: locked ? 'not-allowed' : 'pointer',
              opacity: locked ? 0.6 : 1,
            }}
          >
            <input
              type="checkbox"
              checked={selected.has(r.value)}
              onChange={() => toggle(r.value)}
              disabled={saveMut.isPending || locked}
              style={{ marginTop: 3 }}
            />
            <div>
              <div style={{ fontWeight: 500, fontFamily: 'monospace' }}>
                {r.label} <span style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>({r.value})</span>
                {locked && <span style={{ color: 'var(--text-tertiary)', fontSize: 12 }}> — super-admin only</span>}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{r.hint}</div>
            </div>
          </label>
        ))}
      </div>

      {error && (
        <div style={{ marginTop: 'var(--spacing-sm)', padding: 'var(--spacing-sm)', background: 'var(--status-error-bg)', color: 'var(--status-error-text)', borderRadius: 4 }}>
          {error}
        </div>
      )}
    </Modal>
  );
}
